import { useEffect, useRef, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { registerDirtyCard } from '@/features/patient-record/lib/dirtyRegistry';
import { useDirtyScope } from '@/features/patient-record/lib/dirtyScope';

interface DirtyFooterProps {
  readonly saving: boolean;
  readonly onSave: () => void;
  readonly onDiscard: () => void;
  /**
   * Card name for the unsaved-edit log line (PTV2-1157). Defaults to a generic label; pass something
   * specific ("Personal information") so a flush is traceable to the card it came from.
   */
  readonly label?: string;
  /**
   * Translated names of the fields that differ from the saved record — names only, no values. Shown
   * in the "unsaved changes" prompt when autosave is off, so the user knows what they'd be
   * discarding. Omit and the prompt names the card alone.
   */
  readonly changedFields?: readonly string[];
  /**
   * PTV2-1327 — whether this footer registers itself with the dirty registry. Default `true` (the
   * legacy self-registering footer). Pass `false` when the card already registers via
   * `useEditableDraft`: the footer then renders as pure Save / Discard chrome (driven by the hook's
   * `saving` / `save` / `discard`) and does NOT double-register.
   */
  readonly register?: boolean;
}

/**
 * Footer that appears under a card body when its draft differs from
 * the saved value. Replaces autosave-on-blur with an explicit
 * commit so accidental clicks don't persist changes. Shared so every
 * card-level edit form gets the same Save / Discard chrome.
 */
export function DirtyFooter({
  saving,
  onSave,
  onDiscard,
  label,
  changedFields,
  register = true,
}: DirtyFooterProps): ReactElement {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const scopeId = useDirtyScope();

  // PTV2-1157: this footer exists only while the card is dirty, so its lifetime IS the "unsaved
  // edit" signal. Register it so the pedigree node editor can commit the draft when the user moves
  // to another node (or deselects) instead of silently dropping it. `onSave` is read through a ref
  // so a re-render can't leave a stale closure registered.
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const onDiscardRef = useRef(onDiscard);
  onDiscardRef.current = onDiscard;
  const changedFieldsRef = useRef(changedFields);
  changedFieldsRef.current = changedFields;
  useEffect(() => {
    // PTV2-1327: a card that registers via useEditableDraft passes register={false} so this footer is
    // presentational only (no double-registration).
    if (!register) return undefined;
    return registerDirtyCard({
      scopeId,
      label: label ?? 'card',
      save: () => {
        onSaveRef.current();
      },
      discard: () => {
        onDiscardRef.current();
      },
      changedFields: () => changedFieldsRef.current ?? [],
      // The footer sits inside the card body, so "the card is being edited" is "focus is somewhere
      // in my parent". Used by the idle flush to leave the field under the cursor alone.
      isEditing: () => {
        const container = ref.current?.parentElement ?? null;
        const active = document.activeElement;
        return container !== null && active !== null && container.contains(active);
      },
    });
  }, [scopeId, label, register]);

  // In the pedigree node editor the sub-sections scroll inside a fixed-height panel
  // (.pedigree-node-content); a dirty footer appearing on the last card sits below the fold and its
  // Save button is unreachable. Scroll it into view on mount. Scoped to the node editor (only when
  // inside that scroll container) so the patient record's page scroll is untouched.
  useEffect(() => {
    const el = ref.current;
    if (el !== null && el.closest('.pedigree-node-content') !== null) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, []);

  return (
    <div
      ref={ref}
      role="region"
      aria-label={t('patient.dirtyFooter.ariaLabel')}
      className="mt-4 pt-4 border-t border-border flex items-center justify-end gap-2"
    >
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onDiscard}
        disabled={saving}
      >
        {t('patient.dirtyFooter.discard')}
      </Button>
      <Button
        type="button"
        size="sm"
        onClick={onSave}
        loading={saving}
        loadingText={t('patient.dirtyFooter.saving')}
      >
        {t('patient.dirtyFooter.save')}
      </Button>
    </div>
  );
}
